// ============================================================
// Anmeldestatus — der kleine Speicher, an dem der Wächter in App.tsx hängt.
//
// Drei Zustände, und der Startwert ist WICHTIG:
//   "unbekannt"  GET /me läuft noch → Wartezeichen, KEINE Umleitung
//   "anonym"     abgemeldet → öffentliche Karte (E21), kein Toast
//   "angemeldet" alles offen
// Mit "anonym" als Startwert sähe ein angemeldeter Mensch bei jedem Neuladen
// für den Bruchteil einer Sekunde das Schaufenster.
//
// Die PERSON selbst (`Ich`) liegt NICHT hier, sondern in TanStack Query unter
// `['ich']` (`useIch()`): sie ist eine Serverantwort und wird von `PATCH /me`,
// vom Avatar-Upload und vom Sprachschalter fortgeschrieben. Hier steht nur,
// OB jemand angemeldet ist — zwei Wahrheiten über dieselbe Person wären eine
// zu viel, und genau daran laufen solche Speicher auseinander.
//
// Deshalb gibt es hier auch KEIN `laden()`: den einen Aufruf von GET /me macht
// die Abfrage in App.tsx, und dieser Speicher übernimmt nur ihr Ergebnis. Ein
// eigener `holeMich()`-Aufruf wäre eine zweite Anfrage beim Start, deren
// Antwort niemand im Zwischenspeicher sähe.
//
// Die 401-Registrierung steht am Fuß der Datei und läuft beim LADEN des
// Moduls: `api/client.ts` darf diesen Speicher nicht importieren (Kreis), also
// meldet der Speicher sich dort an. Damit setzt jeder 401 aus irgendeinem
// Aufruf den Status auf "anonym", und der Wächter führt zur Anmeldung.
// ============================================================
import { create } from "zustand";
import { setzeAbmeldeHandler } from "../api/client";
import { abmelden as apiAbmelden } from "../api/hooks";
import { LEERER_FILTER, useUi } from "./ui";

export type AuthStatus = "unbekannt" | "anonym" | "angemeldet";

interface AuthZustand {
  status: AuthStatus;
  /** GET /me war erfolgreich — oder POST /auth/verify. */
  angemeldet(): void;
  /** GET /me hat 401 geantwortet, oder irgendein anderer Aufruf. */
  abgemeldet(): void;
  /** POST /auth/logout und Status zurück auf „anonym". */
  abmelden(): Promise<void>;
}

/**
 * Was die Karte für die angemeldete Person hält, gehört nicht der nächsten
 * (Fehlerrunde 01.10.2026, #22). Filter, Auswahl, Suche und Zeichenmodus lebten
 * im Tab weiter: „Meine Projekte" oder eine Personensuche aus der Sitzung leerte
 * danach die öffentliche Karte, und wer sich im selben Tab neu anmeldete, erbte
 * Filter und Auswahl seines Vorgängers. Beide Wege hinaus — der Abmeldeknopf und
 * der 401 aus irgendeinem Aufruf — gehen hier durch. Geländewahl, Luftbild und
 * Bürofarben bleiben: das sind Einstellungen des Geräts, nicht der Person.
 */
function sitzungsUiVergessen(): void {
  useUi.setState({
    filter: LEERER_FILTER,
    auswahl: null,
    hervorgehoben: null,
    uebersicht: null,
    sprung: null,
    zeichnen: "aus",
    erfolg: null,
  });
}

export const useAuthStore = create<AuthZustand>((set, get) => ({
  status: "unbekannt",

  angemeldet() {
    set({ status: "angemeldet" });
  },

  abgemeldet() {
    // Nur beim Übergang AUS einer Sitzung: der erste 401 beim Start („unbekannt")
    // und jeder weitere im Schaufenster („anonym") nähmen sonst einer
    // Besucherin die Auswahl unter der Hand weg.
    if (get().status === "angemeldet") sitzungsUiVergessen();
    set({ status: "anonym" });
  },

  async abmelden() {
    try {
      await apiAbmelden();
    } catch {
      // Auch ein fehlgeschlagener Abmeldeversuch beendet die Sitzung im
      // Browser — alles andere ließe jemanden vor einem Bildschirm sitzen,
      // der so tut, als wäre er noch angemeldet.
    }
    sitzungsUiVergessen();
    set({ status: "anonym" });
  },
}));

// EINMAL beim Laden des Moduls. main.tsx importiert App.tsx, App.tsx diesen
// Speicher — die Zeile läuft also, bevor die erste Anfrage hinausgeht.
setzeAbmeldeHandler(() => {
  useAuthStore.getState().abgemeldet();
});
